import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, LogOut, ArrowLeft } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import NotificationsBell from './NotificationsBell';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from './ImageUpload';

/**
 * Feb 2026 — Mobile-only header for the admin panel.
 *
 * Desktop layout (sidebar) is unchanged. On viewports below `md`
 * we render a compact sticky bar: company logo on the left, and
 * the notification bell + a modern dropdown menu on the right.
 *
 * The dropdown surfaces EVERY nav entry the current admin is
 * permitted to see (the parent `navItems` list has already been
 * RBAC-filtered by AdminLayout — super admins see everything,
 * sub-admins only see the sections their permissions unlock).
 * Each entry keeps its lucide icon so the dropdown reads at a
 * glance.
 */
const MobileNav = ({ navItems, authHeaders }) => {
  const { logout } = useAdminAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const logoSrc = resolveImageUrl(settings?.logo_image || '') || '';

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.to
      : location.pathname.startsWith(item.to);

  const entries = navItems || [];

  return (
    <div
      className="md:hidden w-full fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-b border-neutral-200 px-3 h-14 flex items-center justify-between gap-2"
      data-testid="admin-mobile-header"
    >
      <Link to="/admin" className="flex items-center" data-testid="admin-mobile-logo">
        <img
          src={logoSrc}
          alt="Mohammed Riaz & Partner LLC"
          className="h-9 w-auto rounded-md object-contain"
        />
      </Link>
      <div className="flex items-center gap-1">
        <NotificationsBell
          role="admin"
          authHeaders={authHeaders}
          variant="admin"
          data-testid="admin-mobile-bell"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Open admin menu"
              data-testid="admin-mobile-menu-trigger"
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#0F172A] text-white hover:bg-[#0078D4] transition-colors shadow-sm"
            >
              <Menu className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={10}
            className="w-72 max-h-[80vh] overflow-y-auto rounded-2xl shadow-2xl border-neutral-200"
            data-testid="admin-mobile-menu-content"
          >
            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              Navigate
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {entries.length === 0 && (
              <div className="px-2 py-3 text-xs text-neutral-500">
                No sections available for this account.
              </div>
            )}
            {entries.map((item) => {
              const active = isActive(item);
              const testid = `admin-mobile-menu-${item.to.replace(/[/]/g, '-').slice(1)}`;
              return (
                <DropdownMenuItem key={item.to} asChild data-testid={testid}>
                  <Link
                    to={item.to}
                    className={`flex items-center gap-3 py-2 cursor-pointer text-sm font-semibold ${
                      active ? 'text-[#0078D4]' : 'text-[#0F172A]'
                    }`}
                  >
                    <span
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${
                        active
                          ? 'bg-[#0078D4] text-white'
                          : 'bg-[#0078D4]/10 text-[#0078D4]'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                    </span>
                    <span className="flex-1">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white min-w-[20px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild data-testid="admin-mobile-menu-back-to-store">
              <Link
                to="/"
                className="flex items-center gap-3 py-2 cursor-pointer text-sm text-neutral-700"
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-100 text-neutral-600">
                  <ArrowLeft className="w-4 h-4" />
                </span>
                <span>Back to store</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                logout();
                navigate('/admin/login', { replace: true });
              }}
              className="flex items-center gap-3 py-2 cursor-pointer text-sm font-semibold text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid="admin-mobile-menu-logout"
            >
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-500/10 text-red-600">
                <LogOut className="w-4 h-4" />
              </span>
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default MobileNav;
